import { ActionButton, DefaultButton, Icon, ISearchBoxStyles, SearchBox, Spinner, SpinnerSize } from "@fluentui/react";
import { useEffect, useRef } from "react";
import { Utils } from "../../common/views/Utils";
import { Page } from "../../grid/models/Page";
import { Pager } from "../../grid/views/Pager";
import { NewFeedModel } from "../models/NewFeedModel";
import { NewFeedPictureService } from "../services/NewFeedPictureService";
import { useNewFeedVM } from "../viewmodels/NewFeedVM";
import NewFeedDetailView from "./NewFeedDetailView";
import NewFeedEntryView from "./NewFeedEntryView";
import NewFeedFilterView from "./NewFeedFilterView";
import NewFeedGridView from "./NewFeedGridView";

export default function NewFeedView() {
    const hasLoaded = useRef(false);
    const vm = useNewFeedVM();
    const pictureService = new NewFeedPictureService();

    useEffect(() => {
        if (hasLoaded.current) return;

        hasLoaded.current = true;

        vm.getList();
    }, [vm]);

    const width2 = Utils.getColumnWidth(2);
    const width10 = Utils.getColumnWidth(10);
    const width12 = Utils.getColumnWidth(12);

    const onPageChange = (page: Page) => {
        if (page) {
            vm.filter.pageNumber = page.pageNumber;

            if (vm.filter.pageSize !== page.pageSize) {
                vm.filter.pageNumber = 1;
            }

            vm.filter.pageSize = page.pageSize;

            vm.getList(true);
        }
    }

    const onAdd = () => {
        vm.setIsEdit(false);
        vm.setShowEntry(true);
    }

    const onEdit = (feed: NewFeedModel) => {
        vm.setInputForm({
            id: feed.id,
            brand: feed.brand,
            title: feed.title,
            description: feed.description,
            url: feed.url,
            start: new Date(feed.start),
            end: new Date(feed.end),
            type: feed.type,
            order: feed.order,
            referenceId: feed.referenceId
        });

        vm.setIsEdit(true);
        vm.setShowEntry(true);
    }

    const onEntryClose = () => {
        vm.setIsEdit(false);
        vm.setShowEntry(false);
    }

    const onDetail = (feed: NewFeedModel) => {
        vm.setSelectedItem(feed);
        vm.setShowDetail(true);
    }

    const onDetailClose = () => {
        if (window.confirm("Would you like to refresh the data?")) {
            vm.getList();
        }

        vm.setShowDetail(false);
    }

    const onActivate = (feed: NewFeedModel) => {
        const status = feed.active ? "deactivate" : "activate";

        if (window.confirm("Would you like to " + status + " the new feed?")) {
            vm.activate(feed.id, !feed.active);
        }
    }

    const createMenuItems = () => {
        let items = [
            {
                key: 'add',
                onClick: onAdd,
                iconProps: { iconName: 'Add' },
                text: 'Add'
            },
            {
                key: 'filter',
                onClick: () => vm.setShowFilter(true),
                iconProps: { iconName: 'Filter' },
                text: 'Filter'
            },
            {
                key: 'refresh',
                onClick: () => vm.getList(),
                iconProps: { iconName: 'Refresh' },
                text: 'Refresh'
            }
        ];

        return items;
    }

    const searchBox = (styles: ISearchBoxStyles) => {
        return <SearchBox
            autoFocus={false}
            value={vm.filter.search}
            placeholder="Search"
            onChange={(_, newValue) => vm.setFilterField("search", newValue || "")}
            onSearch={(value: string) => vm.getList()}
            className="searchbox"
            styles={styles}
        />
    }

    const menuView = () => {
        return <DefaultButton
            menuProps={{
                shouldFocusOnMount: true,
                items: createMenuItems()
            }}
            className="context-menu-text white-background"
            styles={{
                root: { height: 40, minWidth: 40, width: 40 },
                menuIcon: { display: "none !important" }
            }}
            style={{ backgroundColor: "white", height: 32, fontSize: "large", paddingLeft: 8 }}>
            <Icon iconName="BulletedListTextMirrored" />
        </DefaultButton>;
    }

    const mobileHeader = () => {
        return <div className="ms-Grid-row">
            <div className={width10}>
                {searchBox({})}
            </div>
            <div className={width2 + " right"}>{menuView()}</div>
        </div>;
    }

    const desktopHeader = () => {
        return <div className="ms-Grid-row">
            <div className={width12 + " center"}>
                <div>
                    {searchBox({ root: { width: "300px", display: "inline-flex" } })}

                    <ActionButton
                        iconProps={{ iconName: "Refresh" }}
                        onClick={() => vm.getList()}>Refresh</ActionButton>

                    <ActionButton
                        iconProps={{ iconName: "Add" }}
                        onClick={onAdd}>Add New</ActionButton>

                    <ActionButton
                        iconProps={{ iconName: "Filter" }}
                        onClick={() => vm.setShowFilter(true)}>Filter</ActionButton>
                </div>
            </div>
        </div>;
    }

    return <div className="ms-Grid pt-10">
        {window.innerWidth <= 500 && mobileHeader()}
        {window.innerWidth > 500 && desktopHeader()}

        <div className="ms-Grid-row pv-20">
            {vm.loading && <Spinner size={SpinnerSize.large} />}

            <NewFeedGridView
                feeds={vm.feeds}
                key={vm.listHash}
                onEdit={onEdit}
                onDetail={onDetail}
                onActivate={onActivate}
            />
        </div>

        <div className="ms-Grid-row" style={{ width: "100%", display: "inline-block" }}>
            <Pager
                totalCount={vm.totalCount}
                onChange={onPageChange}
                pageSize={vm.filter.pageSize}
                key={vm.pageHash}
            />
        </div>

        {vm.showEntry && <>
            <NewFeedEntryView
                isEdit={vm.isEdit}
                form={vm.inputForm}
                onClose={onEntryClose}
                onValueChange={vm.setInputField}
                loading={vm.loading}
                onEdit={vm.edit}
                onCreate={vm.create}
            />
        </>}

        {vm.showDetail && vm.selectedItem && <>
            <NewFeedDetailView
                feed={vm.selectedItem}
                pictureService={pictureService}
                onClose={onDetailClose}
                onEdit={onEdit}
            />
        </>}

        {vm.showFilter && <>
            <NewFeedFilterView
                form={vm.filter}
                onValueChange={vm.setFilterField}
                onClose={() => vm.setShowFilter(false)}
                onSubmit={() => {
                    vm.getList(false);
                    vm.setShowFilter(false);
                }}
            />
        </>}
    </div>;
}